/**
 * Supabase client — uses env vars when set (see .env.example).
 *
 * Usage:
 *   import { getSupabase } from "./supabase";
 *   const client = getSupabase(); if (client) { const { data } = await client.schema('matcha').from('products').select('*'); }
 *
 * All tables live in the `matcha` schema, not `public`.
 * Use `.schema('matcha')` on every query.
 */

import { createClient, type SupabaseClient } from "@supabase/supabase-js";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL ?? "";
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY ?? "";

export const hasSupabaseCredentials =
  typeof SUPABASE_URL === "string" &&
  SUPABASE_URL.length > 0 &&
  SUPABASE_URL !== "YOUR_SUPABASE_URL" &&
  typeof SUPABASE_ANON_KEY === "string" &&
  SUPABASE_ANON_KEY.length > 0 &&
  SUPABASE_ANON_KEY !== "your_anon_key_here";

/** Lazy singleton: create client on first use to avoid auth lock races with React Strict Mode double-mount. */
let _client: SupabaseClient | null = null;

export function getSupabase(): SupabaseClient | null {
  if (!hasSupabaseCredentials) return null;
  if (!_client) {
    _client = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
      },
    });
  }
  return _client;
}

export function isSupabaseConnected(): boolean {
  return hasSupabaseCredentials;
}

/** Shared first getSession() promise so React Strict Mode / concurrent mounts don't race for the auth lock. */
let _initialSessionPromise: Promise<{ data: { session: any }; error: any }> | null = null;

/**
 * Get session, sharing the first call's promise to avoid lock contention (e.g. React Strict Mode).
 * Use this for initial auth bootstrap; use getSession() for later explicit checks.
 */
export function getInitialSession(): Promise<{ data: { session: any }; error: any }> {
  const client = getSupabase();
  if (!client) return Promise.resolve({ data: { session: null }, error: null });
  if (!_initialSessionPromise) {
    _initialSessionPromise = client.auth.getSession().finally(() => {
      _initialSessionPromise = null;
    });
  }
  return _initialSessionPromise;
}

// ═══════════════════════════════════════════════════════════════════════
//  AUTH — Admin sign-in / sign-out / session management
// ═══════════════════════════════════════════════════════════════════════

/** Sign in an admin user with Supabase Auth (email + password) */
export async function signInAdmin(email: string, password: string) {
  const client = getSupabase();
  if (!client) {
    return { data: null, error: { message: "Supabase not connected" } };
  }
  const { data, error } = await client.auth.signInWithPassword({
    email,
    password,
  });
  return { data, error };
}

/** Sign out the current Supabase session */
export async function signOutAdmin() {
  const client = getSupabase();
  if (!client) return { error: null };
  return client.auth.signOut();
}

/** Get the current Supabase session (returns null if none) */
export async function getSession() {
  const client = getSupabase();
  if (!client) return { data: { session: null }, error: null };
  return client.auth.getSession();
}

/**
 * Subscribe to auth state changes (login, logout, token refresh).
 * Returns an unsubscribe function.
 */
export function onAuthStateChange(callback: (event: string, session: any) => void) {
  const client = getSupabase();
  if (!client) return { data: { subscription: { unsubscribe: () => {} } } };
  return client.auth.onAuthStateChange(callback);
}

// ═══════════════════════════════════════════════════════════════════════
//  ADMIN CRUD — matcha.admin_users (auth_uid links to auth.users)
// ══════════════���════════════════════════════════════════════════════════
//
//  Schema expected:
//    matcha.admin_users (
//      id          uuid primary key default gen_random_uuid(),
//      auth_uid    uuid references auth.users(id) on delete cascade,
//      email       text not null unique,
//      name        text not null,
//      role        text not null check (role in ('admin','editor','journal_editor')),
//      created_at  timestamptz default now(),
//      last_login  timestamptz
//    )
//
//  RLS policies should restrict to admin role only.
// ═══════════════════════════════════════════════════════════════════════

/** Fetch all admin users from Supabase */
export async function fetchAdminUsers() {
  const client = getSupabase();
  if (!client) return { data: null, error: null };
  return client
    .schema("matcha")
    .from("admin_users")
    .select("*")
    .order("created_at", { ascending: true });
}

/** Fetch a single admin user by their auth UUID */
export async function fetchAdminUserByAuthUid(authUid: string) {
  const client = getSupabase();
  if (!client) return { data: null, error: null };
  return client
    .schema("matcha")
    .from("admin_users")
    .select("*")
    .eq("auth_uid", authUid)
    .single();
}

/**
 * Create a new admin user via the create-admin-user Edge Function.
 * The Edge Function uses the service_role key server-side to create the auth user
 * and insert into matcha.admin_users. The client must be signed in as an admin
 * (Bearer token is sent with the request).
 *
 * Deploy the function first: see src/imports/edge-function-create-admin-user.ts
 *   supabase functions deploy create-admin-user
 */
export async function createAdminUser(user: {
  email: string;
  name: string;
  role: "admin" | "editor" | "journal_editor";
  password?: string;
}) {
  const client = getSupabase();
  if (!client) {
    console.debug("[Supabase not connected] Create admin user:", user.email);
    return { data: null, error: null };
  }

  const { data: sessionData } = await client.auth.getSession();
  const accessToken = sessionData?.session?.access_token;
  if (!accessToken) {
    return {
      data: null,
      error: { message: "Not signed in. Please sign in again and retry." },
    };
  }

  const functionsUrl = `${SUPABASE_URL.replace(/\/$/, "")}/functions/v1/create-admin-user`;
  const res = await fetch(functionsUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
      apikey: SUPABASE_ANON_KEY,
    },
    body: JSON.stringify({
      email: user.email,
      name: user.name,
      role: user.role,
      password: user.password ?? undefined,
    }),
  });

  const fnData = await res.json().catch(() => ({}));
  if (!res.ok) {
    if (res.status >= 500) {
      console.error("[create-admin-user] Server error:", res.status, fnData);
    }
    const msg =
      (typeof fnData?.error === "string" ? fnData.error : null) ??
      (res.status === 401 ? "Unauthorized. Sign in again and retry." : res.status === 403 ? "Forbidden. Only administrators can create users." : res.status === 500 ? "Server error. Check the console for details." : res.statusText);
    return { data: null, error: { message: msg } };
  }

  const errMsg = fnData?.error;
  if (errMsg) {
    return { data: null, error: { message: typeof errMsg === "string" ? errMsg : "Failed to create user" } };
  }

  const u = fnData?.user;
  if (!u) {
    return { data: null, error: { message: "Invalid response from server" } };
  }

  return {
    data: {
      id: u.id,
      auth_uid: u.auth_uid,
      email: u.email,
      name: u.name,
      role: u.role,
      created_at: u.created_at ?? new Date().toISOString(),
      last_login: u.last_login,
    },
    error: null,
  };
}

/** Update an admin user's profile (name, role) */
export async function updateAdminUser(id: string, updates: {
  name?: string;
  role?: "admin" | "editor" | "journal_editor";
  email?: string;
  last_login?: string;
}) {
  const client = getSupabase();
  if (!client) {
    console.debug("[Supabase not connected] Update admin user:", id);
    return { data: null, error: null };
  }
  return client
    .schema("matcha")
    .from("admin_users")
    .update(updates)
    .eq("id", id)
    .select()
    .single();
}

/** Delete an admin user (profile row + optionally the auth user) */
export async function deleteAdminUser(id: string) {
  const client = getSupabase();
  if (!client) {
    console.debug("[Supabase not connected] Delete admin user:", id);
    return { data: null, error: null };
  }

  // Delete profile row only. Auth user deletion requires service_role — use an Edge Function if needed.
  const { error } = await client
    .schema("matcha")
    .from("admin_users")
    .delete()
    .eq("id", id);

  return { data: null, error };
}

/** Update last_login timestamp for an admin user */
export async function touchAdminLogin(authUid: string) {
  const client = getSupabase();
  if (!client) return;
  await client
    .schema("matcha")
    .from("admin_users")
    .update({ last_login: new Date().toISOString() })
    .eq("auth_uid", authUid);
}

// ══════════════════���════════════════════════════════════════════════════
//  STOREFRONT CUSTOMER AUTH
// ═══════════════════════════════════════════════════════════════════════
//
//  Schema expected:
//    matcha.customer_profiles (
//      id            uuid primary key default gen_random_uuid(),
//      auth_uid      uuid references auth.users(id) on delete cascade,
//      email         text not null,
//      display_name  text,
//      country       text,
//      created_at    timestamptz default now()
//    )
// ═══════════════════════════════════════════════════════════════════════

/** Sign up a storefront customer */
export async function signUpCustomer(email: string, password: string, displayName?: string) {
  const client = getSupabase();
  if (!client) {
    return { data: null, error: { message: "Supabase not connected" } };
  }
  const { data, error } = await client.auth.signUp({
    email,
    password,
    options: {
      data: { display_name: displayName || "" },
    },
  });
  if (!error && data?.user) {
    // Create customer profile
    await client
      .schema("matcha")
      .from("customer_profiles")
      .insert({
        auth_uid: data.user.id,
        email,
        display_name: displayName || null,
      });
  }
  return { data, error };
}

/** Sign in a storefront customer */
export async function signInCustomer(email: string, password: string) {
  const client = getSupabase();
  if (!client) {
    return { data: null, error: { message: "Supabase not connected" } };
  }
  return client.auth.signInWithPassword({ email, password });
}

/** Fetch the customer profile for the current session */
export async function fetchCustomerProfile(authUid: string) {
  const client = getSupabase();
  if (!client) return { data: null, error: null };
  return client
    .schema("matcha")
    .from("customer_profiles")
    .select("*")
    .eq("auth_uid", authUid)
    .maybeSingle();
}

/** Fetch order history for a customer */
export async function fetchCustomerOrders(customerEmail: string) {
  const client = getSupabase();
  if (!client) return { data: null, error: null };
  return client
    .schema("matcha")
    .from("orders")
    .select("*, order_items(*)")
    .eq("customer_email", customerEmail)
    .order("created_at", { ascending: false });
}

// ═══════════════════════════════════════════��═══════════════════════════
//  EXISTING HELPERS (unchanged)
// ═══════════════════════════════════════════════════════════════════════

/** Insert an order into matcha.orders + matcha.order_items */
export async function insertOrder(order: {
  customer_name: string;
  customer_email: string;
  customer_phone?: string;
  customer_country?: string;
  delivery: "pickup" | "shipping";
  shipping_address?: string;
  notes?: string;
  subtotal: number;
  promo_code?: string;
  discount_amount?: number;
  items: { product_id: number; product_name: string; quantity: number; unit_price: number }[];
}) {
  const client = getSupabase();
  if (!client) {
    console.debug("[Supabase not connected] Order received:", order.items.length, "items, subtotal:", order.subtotal);
    return { data: null, error: null };
  }

  const { data: orderRow, error: orderErr } = await client
    .schema("matcha")
    .from("orders")
    .insert({
      customer_name: order.customer_name,
      customer_email: order.customer_email,
      customer_phone: order.customer_phone || null,
      customer_country: order.customer_country || null,
      delivery: order.delivery,
      shipping_address: order.shipping_address || null,
      notes: order.notes || null,
      subtotal: order.subtotal,
      promo_code: order.promo_code || null,
      discount_amount: order.discount_amount || 0,
    })
    .select("id, order_number")
    .single();

  if (orderErr) return { data: null, error: orderErr };

  const lineItems = order.items.map((item) => ({
    order_id: orderRow.id,
    product_id: item.product_id,
    product_name: item.product_name,
    quantity: item.quantity,
    unit_price: item.unit_price,
  }));

  const { error: itemsErr } = await client
    .schema("matcha")
    .from("order_items")
    .insert(lineItems);

  if (itemsErr) return { data: orderRow, error: itemsErr };

  return { data: orderRow, error: null };
}

/** Fetch all orders with items (for admin). Excludes hidden_from_admin. Requires authenticated admin. */
export async function fetchOrders() {
  const client = getSupabase();
  if (!client) return { data: null, error: null };
  const { data: orders, error: ordersErr } = await client
    .schema("matcha")
    .from("orders")
    .select("*")
    .eq("hidden_from_admin", false)
    .order("created_at", { ascending: false });
  if (ordersErr || !orders?.length) return { data: orders ?? [], error: ordersErr };
  const { data: items, error: itemsErr } = await client
    .schema("matcha")
    .from("order_items")
    .select("*");
  if (itemsErr) return { data: orders, error: null };
  const itemsByOrder = (items ?? []).reduce((acc, i) => {
    const oid = i.order_id;
    if (!acc[oid]) acc[oid] = []; acc[oid].push(i); return acc;
  }, {} as Record<number, typeof items>);
  const ordersWithItems = orders.map((o) => ({
    ...o,
    items: (itemsByOrder[o.id] ?? []).map((i) => ({
      product_id: i.product_id,
      product_name: i.product_name,
      quantity: i.quantity,
      unit_price: i.unit_price,
    })),
  }));
  return { data: ordersWithItems, error: null };
}

/** Update order status. Requires authenticated admin. */
export async function updateOrderStatus(
  orderId: number,
  status: "pending" | "confirmed" | "shipped" | "completed" | "cancelled"
) {
  const client = getSupabase();
  if (!client) return { data: null, error: { message: "Supabase not connected" } };
  return client
    .schema("matcha")
    .from("orders")
    .update({ status })
    .eq("id", orderId)
    .select()
    .single();
}

const ORDER_STATUSES = ["pending", "confirmed", "shipped", "completed", "cancelled"] as const;
export type OrderStatus = (typeof ORDER_STATUSES)[number];

/** Bulk update order status. Requires authenticated admin. */
export async function updateOrderStatusBulk(
  orderIds: number[],
  status: OrderStatus
): Promise<{ data: number | null; error: { message: string } | null }> {
  const client = getSupabase();
  if (!client) return { data: null, error: { message: "Supabase not connected" } };
  if (orderIds.length === 0) return { data: 0, error: null };
  const { data, error } = await client
    .schema("matcha")
    .from("orders")
    .update({ status })
    .in("id", orderIds)
    .select("id");
  if (error) return { data: null, error };
  return { data: data?.length ?? 0, error: null };
}

/** Hide orders from admin list (set status to cancelled and hidden_from_admin = true). Kept in DB for retention. */
export async function hideOrders(
  orderIds: number[]
): Promise<{ data: number | null; error: { message: string } | null }> {
  const client = getSupabase();
  if (!client) return { data: null, error: { message: "Supabase not connected" } };
  if (orderIds.length === 0) return { data: 0, error: null };
  const { data, error } = await client
    .schema("matcha")
    .from("orders")
    .update({ status: "cancelled", hidden_from_admin: true })
    .in("id", orderIds)
    .select("id");
  if (error) return { data: null, error };
  return { data: data?.length ?? 0, error: null };
}

/**
 * Send order request notification email via Edge Function (SECUREFORM: honeypot + Turnstile + rate limits).
 * Requires send-order-request-email deployed and RESEND_API_KEY, ORDER_NOTIFY_EMAIL, TURNSTILE_SECRET_KEY set.
 */
export async function sendOrderRequestEmail(payload: {
  order_number?: string;
  customer_name: string;
  customer_email: string;
  customer_phone?: string;
  customer_country?: string;
  delivery: "pickup" | "shipping";
  shipping_address?: string;
  notes?: string;
  subtotal: number;
  items: { product_id: number; product_name: string; quantity: number; unit_price: number }[];
  website?: string;
  client_id?: string;
  turnstile_token?: string | null;
}): Promise<{ ok: boolean; error?: string }> {
  if (!hasSupabaseCredentials) {
    return { ok: false, error: "Supabase not configured" };
  }
  const url = `${SUPABASE_URL.replace(/\/$/, "")}/functions/v1/send-order-request-email`;
  try {
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: SUPABASE_ANON_KEY,
      },
      body: JSON.stringify(payload),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      return { ok: false, error: (data?.error as string) ?? res.statusText };
    }
    return { ok: true };
  } catch (e) {
    const msg = e instanceof Error ? e.message : "Network error";
    return { ok: false, error: msg };
  }
}

/** Client ID for rate limiting (SECUREFORM). Stored in localStorage. */
export function getClientId(): string {
  const key = "matcha_client_id";
  let id: string | null = null;
  try {
    id = localStorage.getItem(key);
  } catch {
    /* ignore */
  }
  if (!id) {
    id = crypto.randomUUID();
    try {
      localStorage.setItem(key, id);
    } catch {
      /* ignore */
    }
  }
  return id ?? crypto.randomUUID();
}

/** Submit wholesale inquiry via Edge Function (SECUREFORM: honeypot + Turnstile + rate limits). Uses direct fetch like order email to avoid CORS with x-client-info. */
export async function submitWholesaleInquiry(inquiry: {
  company_name: string;
  contact_name: string;
  email: string;
  estimated_volume?: string;
  message?: string;
  partnership_tier?: string;
  website?: string;
  client_id?: string;
  turnstile_token?: string | null;
}): Promise<{ ok: true; inquiryId: number | null } | { ok: false; error: string }> {
  if (!hasSupabaseCredentials) {
    return { ok: false, error: "Supabase not connected" };
  }
  const url = `${SUPABASE_URL.replace(/\/$/, "")}/functions/v1/submit-wholesale-inquiry`;
  try {
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: SUPABASE_ANON_KEY,
      },
      body: JSON.stringify(inquiry),
    });
    const data = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string; inquiryId?: number | null };
    if (!res.ok) {
      return { ok: false, error: (data?.error as string) ?? res.statusText };
    }
    if (data?.ok) {
      return { ok: true, inquiryId: data?.inquiryId ?? null };
    }
    return { ok: false, error: (data?.error as string) ?? "Submission failed" };
  } catch (e) {
    const msg = e instanceof Error ? e.message : "Network error";
    return { ok: false, error: msg };
  }
}

/** Submit workshop subscription via Edge Function (SECUREFORM: Turnstile + rate limits). */
export async function submitWorkshopSubscription(payload: {
  session_id: string;
  email: string;
  first_name?: string | null;
  last_name?: string | null;
  phone?: string | null;
  company?: string | null;
  vat_number?: string | null;
  street?: string | null;
  house_number?: string | null;
  postal_code?: string | null;
  city?: string | null;
  country?: string | null;
  notes?: string | null;
  marketing_opt_in?: boolean;
  privacy_policy_accepted: boolean;
  terms_accepted: boolean;
  source?: string;
  status?: "subscribed" | "waitlist";
  website?: string;
  turnstile_token?: string | null;
}): Promise<{ ok: true; subscriptionId: string | null } | { ok: false; error: string }> {
  if (!hasSupabaseCredentials) {
    return { ok: false, error: "Supabase not connected" };
  }
  const url = `${SUPABASE_URL.replace(/\/$/, "")}/functions/v1/submit-workshop-subscription`;
  try {
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", apikey: SUPABASE_ANON_KEY },
      body: JSON.stringify(payload),
    });
    const data = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string; subscriptionId?: string | null };
    if (!res.ok) return { ok: false, error: (data?.error as string) ?? res.statusText };
    if (data?.ok) return { ok: true, subscriptionId: data?.subscriptionId ?? null };
    return { ok: false, error: (data?.error as string) ?? "Submission failed" };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : "Network error" };
  }
}

/** Fetch wholesale inquiries (admin only; requires authenticated admin). Sorted newest first. */
export async function fetchWholesaleInquiries() {
  const client = getSupabase();
  if (!client) return { data: null, error: null };
  return client
    .schema("matcha")
    .from("wholesale_inquiries")
    .select("*")
    .order("created_at", { ascending: false });
}

/** Fetch all products */
export async function fetchProducts() {
  const client = getSupabase();
  if (!client) return { data: null, error: null };
  return client.schema("matcha").from("products").select("*").order("id");
}

/** Fetch single product by slug */
export async function fetchProductBySlug(slug: string) {
  const client = getSupabase();
  if (!client) return { data: null, error: null };
  return client.schema("matcha").from("products").select("*").eq("slug", slug).single();
}

/** Insert a product; returns the created row with id. Requires authenticated admin. */
export async function insertProduct(row: {
  slug: string;
  name: string;
  price: number;
  category: string;
  tag?: string | null;
  description?: string | null;
  details?: { label: string; value: string }[] | null;
  images?: string[];
  related_ids?: number[];
  stock_quantity?: number;
  low_stock_threshold?: number;
  i18n?: Record<string, unknown>;
}) {
  const client = getSupabase();
  if (!client) return { data: null, error: { message: "Supabase not connected" } };
  return client
    .schema("matcha")
    .from("products")
    .insert({
      slug: row.slug,
      name: row.name,
      price: row.price,
      category: row.category,
      tag: row.tag ?? null,
      description: row.description ?? null,
      details: row.details ?? [],
      images: row.images ?? [],
      related_ids: row.related_ids ?? [],
      stock_quantity: row.stock_quantity ?? 0,
      low_stock_threshold: row.low_stock_threshold ?? 5,
      i18n: row.i18n ?? {},
    })
    .select()
    .single();
}

/** Update a product by id. Requires authenticated admin. */
export async function updateProduct(
  id: number,
  row: {
    slug?: string;
    name?: string;
    price?: number;
    category?: string;
    tag?: string | null;
    description?: string | null;
    details?: { label: string; value: string }[];
    images?: string[];
    related_ids?: number[];
    stock_quantity?: number;
    low_stock_threshold?: number;
    i18n?: Record<string, unknown>;
  }
) {
  const client = getSupabase();
  if (!client) return { data: null, error: { message: "Supabase not connected" } };
  const updates: Record<string, unknown> = {};
  if (row.slug !== undefined) updates.slug = row.slug;
  if (row.name !== undefined) updates.name = row.name;
  if (row.price !== undefined) updates.price = row.price;
  if (row.category !== undefined) updates.category = row.category;
  if (row.tag !== undefined) updates.tag = row.tag;
  if (row.description !== undefined) updates.description = row.description;
  if (row.details !== undefined) updates.details = row.details;
  if (row.images !== undefined) updates.images = row.images;
  if (row.related_ids !== undefined) updates.related_ids = row.related_ids;
  if (row.stock_quantity !== undefined) updates.stock_quantity = row.stock_quantity;
  if (row.low_stock_threshold !== undefined) updates.low_stock_threshold = row.low_stock_threshold;
  if (row.i18n !== undefined) updates.i18n = row.i18n;
  return client
    .schema("matcha")
    .from("products")
    .update(updates)
    .eq("id", id)
    .select()
    .single();
}

/** Delete a product by id. Requires authenticated admin. */
export async function deleteProduct(id: number) {
  const client = getSupabase();
  if (!client) return { error: { message: "Supabase not connected" } };
  return client.schema("matcha").from("products").delete().eq("id", id);
}

/** Fetch all workshops */
export async function fetchWorkshops() {
  const client = getSupabase();
  if (!client) return { data: null, error: null };
  return client.schema("matcha").from("workshops").select("*").order("id");
}

/** Insert a workshop; returns the created row with id. Requires authenticated admin/editor. */
export async function insertWorkshop(row: {
  title: string;
  date: string;
  location: string;
  image?: string;
  description?: string;
  i18n?: Record<string, unknown>;
}) {
  const client = getSupabase();
  if (!client) return { data: null, error: { message: "Supabase not connected" } };
  return client
    .schema("matcha")
    .from("workshops")
    .insert({
      title: row.title,
      date: row.date,
      location: row.location,
      image: row.image ?? null,
      description: row.description ?? null,
      i18n: row.i18n ?? {},
    })
    .select()
    .single();
}

/** Update a workshop by id. Requires authenticated admin/editor. */
export async function updateWorkshop(
  id: number,
  row: {
    title?: string;
    date?: string;
    location?: string;
    image?: string;
    description?: string;
    i18n?: Record<string, unknown>;
  }
) {
  const client = getSupabase();
  if (!client) return { data: null, error: { message: "Supabase not connected" } };
  return client
    .schema("matcha")
    .from("workshops")
    .update({
      ...(row.title !== undefined && { title: row.title }),
      ...(row.date !== undefined && { date: row.date }),
      ...(row.location !== undefined && { location: row.location }),
      ...(row.image !== undefined && { image: row.image }),
      ...(row.description !== undefined && { description: row.description }),
      ...(row.i18n !== undefined && { i18n: row.i18n }),
    })
    .eq("id", id)
    .select()
    .single();
}

/** Delete a workshop by id. Requires authenticated admin/editor. */
export async function deleteWorkshop(id: number) {
  const client = getSupabase();
  if (!client) return { error: { message: "Supabase not connected" } };
  return client.schema("matcha").from("workshops").delete().eq("id", id);
}

// ═══════════════════════════════════════════════════════════════════════
//  WORKSHOP SESSIONS & SUBSCRIPTIONS (reserve a spot) + GDPR
// ═══════════════════════════════════════════════════════════════════════

export type WorkshopSessionWithCount = {
  id: string;
  template_id: string;
  title_override: string | null;
  description_override: string | null;
  location_override: string | null;
  starts_at: string;
  ends_at: string | null;
  max_capacity_override: number | null;
  status: string;
  is_visible: boolean;
  notes_internal: string | null;
  resend_campaign_id: string | null;
  created_at: string;
  updated_at: string;
  slug: string;
  template_title: string;
  short_description: string | null;
  template_description: string | null;
  template_location: string | null;
  template_image_url: string | null;
  duration_minutes: number | null;
  max_capacity: number | null;
  subscriber_count: number;
};

export type WorkshopSubscription = {
  id: string;
  session_id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  company: string | null;
  vat_number: string | null;
  street: string | null;
  house_number: string | null;
  postal_code: string | null;
  city: string | null;
  country: string | null;
  notes: string | null;
  marketing_opt_in: boolean;
  privacy_policy_accepted: boolean;
  terms_accepted: boolean;
  status: string;
  source: string;
  created_at: string;
  updated_at: string;
};

/** Fetch upcoming visible sessions with subscriber counts (public list + admin). */
export async function fetchWorkshopSessionsWithCounts(opts?: { upcomingOnly?: boolean }) {
  const client = getSupabase();
  if (!client) return { data: [] as WorkshopSessionWithCount[], error: null };
  let q = client.schema("matcha").from("workshop_sessions_with_counts").select("*").order("starts_at", { ascending: true });
  if (opts?.upcomingOnly) q = q.gte("starts_at", new Date().toISOString()).in("status", ["scheduled", "full"]);
  const { data, error } = await q;
  return { data: (data ?? []) as WorkshopSessionWithCount[], error };
}

/** Fetch one session by id (for reserve page). */
export async function fetchWorkshopSessionById(sessionId: string) {
  const client = getSupabase();
  if (!client) return { data: null, error: null };
  const { data, error } = await client
    .schema("matcha")
    .from("workshop_sessions_with_counts")
    .select("*")
    .eq("id", sessionId)
    .single();
  return { data: data as WorkshopSessionWithCount | null, error };
}

/** Insert workshop subscription (reserve a spot or join waitlist). Public when session is visible & scheduled. */
export async function insertWorkshopSubscription(row: {
  session_id: string;
  email: string;
  first_name?: string | null;
  last_name?: string | null;
  phone?: string | null;
  company?: string | null;
  vat_number?: string | null;
  street?: string | null;
  house_number?: string | null;
  postal_code?: string | null;
  city?: string | null;
  country?: string | null;
  notes?: string | null;
  marketing_opt_in?: boolean;
  privacy_policy_accepted: boolean;
  terms_accepted: boolean;
  source?: string;
  /** When session is full, pass 'waitlist' to add to waiting list; default 'subscribed'. */
  status?: "subscribed" | "waitlist";
}) {
  const client = getSupabase();
  if (!client) return { data: null, error: { message: "Supabase not connected" } };
  return client
    .schema("matcha")
    .from("workshop_subscriptions")
    .insert({
      session_id: row.session_id,
      email: row.email.trim().toLowerCase(),
      first_name: row.first_name?.trim() || null,
      last_name: row.last_name?.trim() || null,
      phone: row.phone?.trim() || null,
      company: row.company?.trim() || null,
      vat_number: row.vat_number?.trim() || null,
      street: row.street?.trim() || null,
      house_number: row.house_number?.trim() || null,
      postal_code: row.postal_code?.trim() || null,
      city: row.city?.trim() || null,
      country: row.country?.trim() || null,
      notes: row.notes?.trim() || null,
      marketing_opt_in: row.marketing_opt_in ?? false,
      privacy_policy_accepted: row.privacy_policy_accepted,
      terms_accepted: row.terms_accepted,
      source: row.source ?? "website",
      status: row.status ?? "subscribed",
    })
    .select()
    .single();
}

/** Fetch subscriptions for a session (admin). */
export async function fetchWorkshopSubscriptionsBySession(sessionId: string) {
  const client = getSupabase();
  if (!client) return { data: [] as WorkshopSubscription[], error: null };
  const { data, error } = await client
    .schema("matcha")
    .from("workshop_subscriptions")
    .select("*")
    .eq("session_id", sessionId)
    .order("created_at", { ascending: false });
  return { data: (data ?? []) as WorkshopSubscription[], error };
}

/** Update workshop subscription status (admin). */
export async function updateWorkshopSubscriptionStatus(id: string, status: string) {
  const client = getSupabase();
  if (!client) return { data: null, error: { message: "Supabase not connected" } };
  return client
    .schema("matcha")
    .from("workshop_subscriptions")
    .update({ status })
    .eq("id", id)
    .select()
    .single();
}

/** Fetch all workshop templates (admin + public for dropdown). */
export async function fetchWorkshopTemplates() {
  const client = getSupabase();
  if (!client) return { data: null, error: null };
  const { data, error } = await client.schema("matcha").from("workshop_templates").select("*").order("title");
  return { data, error };
}

/** Insert workshop template (admin). Full workshop definition. */
export async function insertWorkshopTemplate(row: {
  slug: string;
  title: string;
  short_description?: string | null;
  description?: string | null;
  location?: string | null;
  duration_minutes?: number | null;
  max_capacity?: number | null;
  image_url?: string | null;
  is_active?: boolean;
}) {
  const client = getSupabase();
  if (!client) return { data: null, error: { message: "Supabase not connected" } };
  return client
    .schema("matcha")
    .from("workshop_templates")
    .insert({
      slug: row.slug,
      title: row.title,
      short_description: row.short_description ?? null,
      description: row.description ?? null,
      location: row.location ?? null,
      duration_minutes: row.duration_minutes ?? null,
      max_capacity: row.max_capacity ?? null,
      image_url: row.image_url ?? null,
      is_active: row.is_active ?? true,
    })
    .select()
    .single();
}

/** Update workshop template (admin). */
export async function updateWorkshopTemplate(
  id: string,
  row: Partial<{
    slug: string;
    title: string;
    short_description: string | null;
    description: string | null;
    location: string | null;
    duration_minutes: number | null;
    max_capacity: number | null;
    image_url: string | null;
    is_active: boolean;
  }>
) {
  const client = getSupabase();
  if (!client) return { data: null, error: { message: "Supabase not connected" } };
  return client.schema("matcha").from("workshop_templates").update(row).eq("id", id).select().single();
}

/** Delete workshop template (admin). Cascades to sessions and subscriptions. */
export async function deleteWorkshopTemplate(id: string) {
  const client = getSupabase();
  if (!client) return { error: { message: "Supabase not connected" } };
  return client.schema("matcha").from("workshop_templates").delete().eq("id", id);
}

/** Insert workshop session (admin). */
export async function insertWorkshopSession(row: {
  template_id: string;
  title_override?: string | null;
  description_override?: string | null;
  location_override?: string | null;
  starts_at: string;
  ends_at?: string | null;
  max_capacity_override?: number | null;
  status?: string;
  is_visible?: boolean;
  notes_internal?: string | null;
}) {
  const client = getSupabase();
  if (!client) return { data: null, error: { message: "Supabase not connected" } };
  return client
    .schema("matcha")
    .from("workshop_sessions")
    .insert({
      template_id: row.template_id,
      title_override: row.title_override ?? null,
      description_override: row.description_override ?? null,
      location_override: row.location_override ?? null,
      starts_at: row.starts_at,
      ends_at: row.ends_at ?? null,
      max_capacity_override: row.max_capacity_override ?? null,
      status: row.status ?? "scheduled",
      is_visible: row.is_visible ?? true,
      notes_internal: row.notes_internal ?? null,
    })
    .select()
    .single();
}

/** Update workshop session (admin): status, is_visible, etc. */
export async function updateWorkshopSession(
  id: string,
  row: Partial<{
    title_override: string | null;
    description_override: string | null;
    location_override: string | null;
    starts_at: string;
    ends_at: string | null;
    max_capacity_override: number | null;
    status: string;
    is_visible: boolean;
    notes_internal: string | null;
  }>
) {
  const client = getSupabase();
  if (!client) return { data: null, error: { message: "Supabase not connected" } };
  return client.schema("matcha").from("workshop_sessions").update(row).eq("id", id).select().single();
}

/** Delete workshop session (admin). Cascades to subscriptions. */
export async function deleteWorkshopSession(id: string) {
  const client = getSupabase();
  if (!client) return { error: { message: "Supabase not connected" } };
  return client.schema("matcha").from("workshop_sessions").delete().eq("id", id);
}

/** Duplicate workshop session to new date (admin). Returns new session id. */
export async function duplicateWorkshopSession(
  sessionId: string,
  newStartsAt: string,
  newEndsAt?: string | null
): Promise<{ data: string | null; error: { message: string } | null }> {
  const client = getSupabase();
  if (!client) return { data: null, error: { message: "Supabase not connected" } };
  const { data, error } = await client.schema("matcha").rpc("duplicate_workshop_session", {
    p_session_id: sessionId,
    p_new_starts_at: newStartsAt,
    p_new_ends_at: newEndsAt ?? null,
  });
  if (error) return { data: null, error };
  return { data: data as string | null, error: null };
}

/** GDPR: export all data for an email (admin only). Uses RPC. */
export async function exportDataByEmail(email: string): Promise<{ data: { workshop_subscriptions: unknown[]; orders: unknown[]; email: string } | null; error: { message: string } | null }> {
  const client = getSupabase();
  if (!client) return { data: null, error: { message: "Supabase not connected" } };
  const { data, error } = await client.schema("matcha").rpc("export_data_by_email", { p_email: email.trim().toLowerCase() });
  if (error) return { data: null, error };
  return { data: data as { workshop_subscriptions: unknown[]; orders: unknown[]; email: string }, error: null };
}

/** Insert GDPR export request (admin audit). */
export async function insertGdprExportRequest(row: {
  requested_email: string;
  request_type: "export" | "delete" | "anonymize";
  status?: string;
  requested_by_user_id?: string | null;
  notes?: string | null;
}) {
  const client = getSupabase();
  if (!client) return { data: null, error: { message: "Supabase not connected" } };
  return client
    .schema("matcha")
    .from("gdpr_export_requests")
    .insert({
      requested_email: row.requested_email,
      request_type: row.request_type,
      status: row.status ?? "pending",
      requested_by_user_id: row.requested_by_user_id ?? null,
      notes: row.notes ?? null,
    })
    .select("id")
    .single();
}

/** Fetch published journal entries */
export async function fetchJournalEntries() {
  const client = getSupabase();
  if (!client) return { data: null, error: null };
  return client
    .schema("matcha")
    .from("journal_entries")
    .select("*")
    .eq("published", true)
    .order("created_at", { ascending: false });
}

/** Fetch a single journal entry by slug */
export async function fetchJournalBySlug(slug: string) {
  const client = getSupabase();
  if (!client) return { data: null, error: null };
  return client
    .schema("matcha")
    .from("journal_entries")
    .select("*")
    .eq("slug", slug)
    .eq("published", true)
    .single();
}

/** Fetch all journal entries (for admin: includes drafts) */
export async function fetchAllJournalEntries() {
  const client = getSupabase();
  if (!client) return { data: null, error: null };
  return client
    .schema("matcha")
    .from("journal_entries")
    .select("*")
    .order("created_at", { ascending: false });
}

/** Insert a journal entry; returns the created row with id. Requires authenticated admin/journal_editor. */
export async function insertJournalEntry(row: {
  title: string;
  slug: string;
  excerpt?: string;
  content?: string;
  image?: string;
  author?: string;
  published?: boolean;
  i18n?: Record<string, unknown>;
}) {
  const client = getSupabase();
  if (!client) return { data: null, error: { message: "Supabase not connected" } };
  return client
    .schema("matcha")
    .from("journal_entries")
    .insert({
      title: row.title,
      slug: row.slug,
      excerpt: row.excerpt ?? null,
      content: row.content ?? null,
      image: row.image ?? null,
      author: row.author ?? null,
      published: row.published ?? false,
      i18n: row.i18n ?? {},
    })
    .select()
    .single();
}

/** Update a journal entry by id. Requires authenticated admin/journal_editor. */
export async function updateJournalEntry(
  id: number,
  row: {
    title?: string;
    slug?: string;
    excerpt?: string;
    content?: string;
    image?: string;
    author?: string;
    published?: boolean;
    i18n?: Record<string, unknown>;
  }
) {
  const client = getSupabase();
  if (!client) return { data: null, error: { message: "Supabase not connected" } };
  return client
    .schema("matcha")
    .from("journal_entries")
    .update({
      ...(row.title !== undefined && { title: row.title }),
      ...(row.slug !== undefined && { slug: row.slug }),
      ...(row.excerpt !== undefined && { excerpt: row.excerpt }),
      ...(row.content !== undefined && { content: row.content }),
      ...(row.image !== undefined && { image: row.image }),
      ...(row.author !== undefined && { author: row.author }),
      ...(row.published !== undefined && { published: row.published }),
      ...(row.i18n !== undefined && { i18n: row.i18n }),
    })
    .eq("id", id)
    .select()
    .single();
}

/** Delete a journal entry by id. Requires authenticated admin/journal_editor. */
export async function deleteJournalEntry(id: number) {
  const client = getSupabase();
  if (!client) return { error: { message: "Supabase not connected" } };
  return client.schema("matcha").from("journal_entries").delete().eq("id", id);
}

/** Fetch site configuration */
export async function fetchSiteConfig() {
  const client = getSupabase();
  if (!client) return { data: null, error: null };
  return client
    .schema("matcha")
    .from("site_config")
    .select("*")
    .eq("key", "default")
    .single();
}

/** Convert flat site_config row from DB to nested shape (hero, about, features, logo). Caller merges with defaultSiteConfig for theme/faq/wholesale. */
export function flatToSiteConfig(row: Record<string, unknown> | null): Record<string, unknown> {
  if (!row || typeof row !== "object") return {};
  return {
    logo: row.logo_url,
    contact_email: row.contact_email ?? undefined,
    contact_phone: row.contact_phone ?? undefined,
    contact_address: row.contact_address ?? undefined,
    hero: {
      title: row.hero_title ?? "",
      subtitle: row.hero_subtitle ?? "",
      cta_text: row.hero_cta_text ?? "",
      image: row.hero_image ?? "",
      i18n: row.hero_i18n ?? {},
    },
    about: {
      label: row.about_label ?? "",
      title: row.about_title ?? "",
      paragraph1: row.about_paragraph1 ?? "",
      paragraph2: row.about_paragraph2 ?? "",
      image: row.about_image ?? "",
      cta_text: row.about_cta_text ?? "",
      i18n: row.about_i18n ?? {},
    },
    features: {
      reviews: !!row.feature_reviews,
      newsletter: !!row.feature_newsletter,
      promoCodes: !!row.feature_promo_codes,
      faq: !!row.feature_faq,
      wishlist: row.feature_wishlist !== false,
      account: row.feature_account === true,
    },
  };
}

/** Convert nested site config to flat object for DB upsert. Only includes columns that exist in matcha.site_config. */
export function siteConfigToFlat(config: Record<string, unknown>): Record<string, unknown> {
  const hero = (config.hero ?? {}) as Record<string, unknown>;
  const about = (config.about ?? {}) as Record<string, unknown>;
  const features = (config.features ?? {}) as Record<string, unknown>;
  return {
    key: "default",
    logo_url: config.logo ?? null,
    contact_email: (config.contact_email as string)?.trim() || null,
    contact_phone: (config.contact_phone as string)?.trim() || null,
    contact_address: (config.contact_address as string)?.trim() || null,
    hero_title: hero.title ?? null,
    hero_subtitle: hero.subtitle ?? null,
    hero_cta_text: hero.cta_text ?? null,
    hero_image: hero.image ?? null,
    hero_i18n: hero.i18n ?? {},
    about_label: about.label ?? null,
    about_title: about.title ?? null,
    about_paragraph1: about.paragraph1 ?? null,
    about_paragraph2: about.paragraph2 ?? null,
    about_image: about.image ?? null,
    about_cta_text: about.cta_text ?? null,
    about_i18n: about.i18n ?? {},
    feature_reviews: !!features.reviews,
    feature_newsletter: !!features.newsletter,
    feature_promo_codes: !!features.promoCodes,
    feature_faq: !!features.faq,
    feature_wishlist: !!features.wishlist,
    feature_account: !!features.account,
  };
}

/** Upsert site configuration */
export async function upsertSiteConfig(config: Record<string, unknown>) {
  const client = getSupabase();
  if (!client) {
    console.debug("[Supabase not connected] Site config upsert");
    return { data: null, error: null };
  }
  return client
    .schema("matcha")
    .from("site_config")
    .upsert({ key: "default", ...config })
    .select()
    .single();
}

/** Fetch approved reviews for a product */
export async function fetchReviews(productId?: number) {
  const client = getSupabase();
  if (!client) return { data: null, error: null };
  let query = client
    .schema("matcha")
    .from("reviews")
    .select("*")
    .eq("approved", true)
    .order("created_at", { ascending: false });
  if (productId) query = query.eq("product_id", productId);
  return query;
}

/** Submit a review */
export async function insertReview(review: {
  product_id: number;
  name: string;
  rating: number;
  comment?: string;
}) {
  const client = getSupabase();
  if (!client) {
    console.debug("[Supabase not connected] Review received for product:", review.product_id);
    return { data: null, error: null };
  }
  return client.schema("matcha").from("reviews").insert(review).select().single();
}

/** Fetch all reviews (for admin). Requires authenticated. */
export async function fetchAllReviews() {
  const client = getSupabase();
  if (!client) return { data: null, error: null };
  return client
    .schema("matcha")
    .from("reviews")
    .select("*")
    .order("created_at", { ascending: false });
}

/** Update a review by id (e.g. set approved). Requires authenticated admin. */
export async function updateReview(id: number, row: { approved?: boolean }) {
  const client = getSupabase();
  if (!client) return { data: null, error: { message: "Supabase not connected" } };
  return client
    .schema("matcha")
    .from("reviews")
    .update({ ...(row.approved !== undefined && { approved: row.approved }) })
    .eq("id", id)
    .select()
    .single();
}

/** Delete a review by id. Requires authenticated admin. */
export async function deleteReview(id: number) {
  const client = getSupabase();
  if (!client) return { error: { message: "Supabase not connected" } };
  return client.schema("matcha").from("reviews").delete().eq("id", id);
}

/** Validate a promo code at checkout */
export async function validatePromoCode(code: string) {
  const client = getSupabase();
  if (!client) return { data: null, error: null };
  return client
    .schema("matcha")
    .from("promo_codes")
    .select("*")
    .ilike("code", code)
    .eq("active", true)
    .single();
}

/** Fetch all promo codes (for admin). */
export async function fetchPromoCodes() {
  const client = getSupabase();
  if (!client) return { data: null, error: null };
  return client.schema("matcha").from("promo_codes").select("*").order("id");
}

/** Insert a promo code. Requires authenticated admin. */
export async function insertPromoCode(row: {
  code: string;
  discount_type: "percentage" | "fixed";
  discount_value: number;
  active?: boolean;
  expires_at?: string | null;
}) {
  const client = getSupabase();
  if (!client) return { data: null, error: { message: "Supabase not connected" } };
  return client
    .schema("matcha")
    .from("promo_codes")
    .insert({
      code: row.code,
      discount_type: row.discount_type,
      discount_value: row.discount_value,
      active: row.active ?? true,
      expires_at: row.expires_at ?? null,
    })
    .select()
    .single();
}

/** Update a promo code by id. Requires authenticated admin. */
export async function updatePromoCode(
  id: number,
  row: {
    code?: string;
    discount_type?: "percentage" | "fixed";
    discount_value?: number;
    active?: boolean;
    expires_at?: string | null;
  }
) {
  const client = getSupabase();
  if (!client) return { data: null, error: { message: "Supabase not connected" } };
  const updates: Record<string, unknown> = {};
  if (row.code !== undefined) updates.code = row.code;
  if (row.discount_type !== undefined) updates.discount_type = row.discount_type;
  if (row.discount_value !== undefined) updates.discount_value = row.discount_value;
  if (row.active !== undefined) updates.active = row.active;
  if (row.expires_at !== undefined) updates.expires_at = row.expires_at;
  return client
    .schema("matcha")
    .from("promo_codes")
    .update(updates)
    .eq("id", id)
    .select()
    .single();
}

/** Delete a promo code by id. Requires authenticated admin. */
export async function deletePromoCode(id: number) {
  const client = getSupabase();
  if (!client) return { error: { message: "Supabase not connected" } };
  return client.schema("matcha").from("promo_codes").delete().eq("id", id);
}

/** Fetch published legal pages */
export async function fetchLegalPages() {
  const client = getSupabase();
  if (!client) return { data: null, error: null };
  return client
    .schema("matcha")
    .from("legal_pages")
    .select("*")
    .eq("published", true)
    .order("sort_order");
}

/** Fetch a single legal page by slug */
export async function fetchLegalPageBySlug(slug: string) {
  const client = getSupabase();
  if (!client) return { data: null, error: null };
  return client
    .schema("matcha")
    .from("legal_pages")
    .select("*")
    .eq("slug", slug)
    .eq("published", true)
    .single();
}

/** Fetch the About page content (first row). Used by the public /about page. */
export async function fetchAboutPage() {
  const client = getSupabase();
  if (!client) return { data: null, error: null };
  return client
    .schema("matcha")
    .from("about_page")
    .select("*")
    .order("id", { ascending: true })
    .limit(1)
    .maybeSingle();
}

// ═══════════════════════════════════════════════════════════════════════
//  PRESS KIT — sections, images, assets (public /press page + admin)
// ═══════════════════════════════════════════════════════════════════════

export type PressKitSection = {
  id: string;
  slug: string;
  title: string;
  content: string | null;
  position: number;
  visible: boolean;
  created_at: string;
  updated_at: string;
  i18n?: Record<string, Record<string, string>> | null;
};

export type PressKitImage = {
  id: string;
  section_slug: string | null;
  image_url: string;
  alt_text: string | null;
  category: string | null;
  position: number;
  created_at: string;
};

export type PressKitAsset = {
  id: string;
  name: string;
  file_url: string;
  type: string | null;
  created_at: string;
};

/** Fetch visible press kit sections (public page). Ordered by position. */
export async function fetchPressKitSections(visibleOnly = true) {
  const client = getSupabase();
  if (!client) return { data: [] as PressKitSection[], error: null };
  let q = client.schema("matcha").from("press_kit_sections").select("*").order("position");
  if (visibleOnly) q = q.eq("visible", true);
  const { data, error } = await q;
  return { data: (data ?? []) as PressKitSection[], error };
}

/** Fetch all press kit sections (admin). */
export async function fetchPressKitSectionsAdmin() {
  return fetchPressKitSections(false);
}

/** Fetch press kit images. Optional filter by section_slug or category. */
export async function fetchPressKitImages(opts?: { sectionSlug?: string; category?: string }) {
  const client = getSupabase();
  if (!client) return { data: [] as PressKitImage[], error: null };
  let q = client.schema("matcha").from("press_kit_images").select("*").order("position");
  if (opts?.sectionSlug) q = q.eq("section_slug", opts.sectionSlug);
  if (opts?.category) q = q.eq("category", opts.category);
  const { data, error } = await q;
  return { data: (data ?? []) as PressKitImage[], error };
}

/** Fetch all press kit assets (downloads). */
export async function fetchPressKitAssets() {
  const client = getSupabase();
  if (!client) return { data: [] as PressKitAsset[], error: null };
  const { data, error } = await client
    .schema("matcha")
    .from("press_kit_assets")
    .select("*")
    .order("created_at", { ascending: false });
  return { data: (data ?? []) as PressKitAsset[], error };
}

/** Insert press kit section (admin). */
export async function insertPressKitSection(row: {
  slug: string;
  title: string;
  content?: string | null;
  position?: number;
  visible?: boolean;
  i18n?: Record<string, Record<string, string>> | null;
}) {
  const client = getSupabase();
  if (!client) return { data: null, error: { message: "Supabase not connected" } };
  return client.schema("matcha").from("press_kit_sections").insert(row).select("id").single();
}

/** Update press kit section (admin). */
export async function updatePressKitSection(
  id: string,
  row: Partial<{
    slug: string;
    title: string;
    content: string | null;
    position: number;
    visible: boolean;
    i18n: Record<string, Record<string, string>> | null;
  }>
) {
  const client = getSupabase();
  if (!client) return { data: null, error: { message: "Supabase not connected" } };
  return client.schema("matcha").from("press_kit_sections").update(row).eq("id", id).select().single();
}

/** Delete press kit section (admin). */
export async function deletePressKitSection(id: string) {
  const client = getSupabase();
  if (!client) return { error: { message: "Supabase not connected" } };
  return client.schema("matcha").from("press_kit_sections").delete().eq("id", id);
}

/** Insert press kit image (admin). */
export async function insertPressKitImage(row: {
  section_slug?: string | null;
  image_url: string;
  alt_text?: string | null;
  category?: string | null;
  position?: number;
}) {
  const client = getSupabase();
  if (!client) return { data: null, error: { message: "Supabase not connected" } };
  return client.schema("matcha").from("press_kit_images").insert(row).select("id").single();
}

/** Update press kit image (admin). */
export async function updatePressKitImage(
  id: string,
  row: Partial<{ section_slug: string | null; image_url: string; alt_text: string | null; category: string | null; position: number }>
) {
  const client = getSupabase();
  if (!client) return { data: null, error: { message: "Supabase not connected" } };
  return client.schema("matcha").from("press_kit_images").update(row).eq("id", id).select().single();
}

/** Delete press kit image (admin). */
export async function deletePressKitImage(id: string) {
  const client = getSupabase();
  if (!client) return { error: { message: "Supabase not connected" } };
  return client.schema("matcha").from("press_kit_images").delete().eq("id", id);
}

/** Insert press kit asset (admin). */
export async function insertPressKitAsset(row: { name: string; file_url: string; type?: string | null }) {
  const client = getSupabase();
  if (!client) return { data: null, error: { message: "Supabase not connected" } };
  return client.schema("matcha").from("press_kit_assets").insert(row).select("id").single();
}

/** Delete press kit asset (admin). */
export async function deletePressKitAsset(id: string) {
  const client = getSupabase();
  if (!client) return { error: { message: "Supabase not connected" } };
  return client.schema("matcha").from("press_kit_assets").delete().eq("id", id);
}

const PRESS_STORAGE_BUCKET = "moecha-press";

/** Upload file to press bucket (admin). Returns public URL. */
export async function uploadPressFile(file: File, folder: string): Promise<{ url: string | null; error: string | null }> {
  const client = getSupabase();
  if (!client) return { url: null, error: "Supabase not connected" };
  const ext = file.name.split(".").pop()?.toLowerCase() || "";
  const path = `${folder}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext || "bin"}`;
  const { data, error } = await client.storage.from(PRESS_STORAGE_BUCKET).upload(path, file, {
    cacheControl: "3600",
    upsert: false,
    contentType: file.type,
  });
  if (error) return { url: null, error: error.message };
  const { data: urlData } = client.storage.from(PRESS_STORAGE_BUCKET).getPublicUrl(data.path);
  return { url: urlData?.publicUrl ?? null, error: null };
}

// ═══════════════════════════════════════════════════════════════════════
//  STORAGE — Image uploads to Supabase Storage bucket "moecha-images"
// ═══════════════════════════════════════════════════════════════════════
//
//  Bucket must be created in Supabase dashboard:
//    Name: moecha-images
//    Public: true (so images can be served without auth)
//    Allowed MIME: image/png, image/jpeg, image/webp, image/svg+xml
//    Max file size: 5 MB
// ═══════════════════════════════════════════════════════════════════════

const STORAGE_BUCKET = "moecha-images";

/** Upload an image file to Supabase Storage and return the public URL */
export async function uploadImage(file: File, folder: string = "general"): Promise<{ url: string | null; error: string | null }> {
  const client = getSupabase();
  if (!client) {
    return { url: null, error: "Supabase not connected — image stored as base64 locally" };
  }

  const ext = file.name.split(".").pop()?.toLowerCase() || "png";
  const timestamp = Date.now();
  const randomSuffix = Math.random().toString(36).substring(2, 8);
  const path = `${folder}/${timestamp}-${randomSuffix}.${ext}`;

  const { data, error } = await client.storage
    .from(STORAGE_BUCKET)
    .upload(path, file, {
      cacheControl: "3600",
      upsert: false,
      contentType: file.type,
    });

  if (error) {
    return { url: null, error: error.message };
  }

  // Get public URL
  const { data: urlData } = client.storage
    .from(STORAGE_BUCKET)
    .getPublicUrl(data.path);

  return { url: urlData?.publicUrl || null, error: null };
}

/** Delete an image from Supabase Storage by its full URL */
export async function deleteStorageImage(publicUrl: string): Promise<void> {
  const client = getSupabase();
  if (!client) return;
  try {
    // Extract path from public URL
    const bucketUrl = `/storage/v1/object/public/${STORAGE_BUCKET}/`;
    const idx = publicUrl.indexOf(bucketUrl);
    if (idx === -1) return;
    const path = publicUrl.substring(idx + bucketUrl.length);
    await client.storage.from(STORAGE_BUCKET).remove([path]);
  } catch {
    console.warn("[MOECHA] Failed to delete storage image");
  }
}